import axios from 'axios';
import {Shop, FcShopConfig} from '@/domain/fcShop.entity';

export interface GetEstateCountForm {
  shopId?: number,
  prefId?: string | number,
  cityIds?: number[],
  townId?: string | number,
  zipCode?: string,
  latitude?: number,
  longitude?: number,
}

export interface EstateCount {
  shopId: number,
  encodedShopId?: string,
  shopName: string,
  fullAddress: string,
  total: number,
  prefId: number,
  prefName: string,
  address: string,
  cityId: number,
  cityName: string,
  landCount: number,
  newEstateCount: number,
  newMansionCount: number,
  oldEstateCount: number,
  oldMansionCount: number,
  businessCount: number,
}

type ShopStatisticResponseBody = {
  id: number;
  encodedShopId?: string;
  fullAddress: string;
  franchiseId: number;
  name: string;
  email: string | null;
  url: string | null;
  estatesToSale: {
    total: number,
    house: { new: number, used: number, },
    apartment: { new: number, used: number, }
    business: number,
    land: number,
  };
}[];

export interface GetFcShopHouseDoDetailForm {
  fcShopId: number
}

export interface FcShopHouseDoDetail {
  id: number
  shopName: string
  url: string
  name: string
  freeDial: string
  tel: string
  zipCode: string
  address: string
  businessHour: string
  holiday: string
  licenseNumber: string
  imageName: string
  cityName: string
  prefName: string
  daysOff: number[]
  workTimes: {
    sunday: { open: string | null, close: string | null }
    monday: { open: string | null, close: string | null }
    tuesday: { open: string | null, close: string | null }
    wednesday: { open: string | null, close: string | null }
    thursday: { open: string | null, close: string | null }
    friday: { open: string | null, close: string | null }
    saturday: { open: string | null, close: string | null }
    latitude: number
    longitude: number
    address: string
  }
}

export interface WorkTime {
  open: `${number}:${number}:${number}`| null,
  close: `${number}:${number}:${number}`| null,
}

export interface GetConfigForm {
  fcShopId: number[],
}

export default {

  async getFcShopConfig(form: GetConfigForm): Promise<FcShopConfig[]> {
    const response = await axios.post('shop/config', {
      fcShopIds: form.fcShopId,
    });
    return response.data.map((res: any) => ({
      fcShopId: Number(res.FcShopId),
      hasOnlineReserveForm: res.OnlineReserveForm,
    })) as FcShopConfig[];
  },

  async listShopsByLocation(form: GetEstateCountForm): Promise<Shop[]> {
    const res = await axios.get('/shop/getMyPageList', {
      params: {
        prefId: String(form.prefId).padStart(2, '0'),
        cityId: form.cityIds?.[0] ? Number(form.cityIds[0]) : undefined,
        townId: String(form.townId).padStart(6, '0'),
        zipCode: form.zipCode ? form.zipCode : undefined,
        latitude: form.latitude? form.latitude : undefined,
        longitude: form.latitude? form.longitude : undefined,
      },
    });
    const data = res.data as {
      id: string,
      encodedShopId: string,
      name: string,
      prefId: string,
      prefName: string,
      address: string,
      cityId: string,
      cityName: string,
      tel: string,
      zipCode: string,
      landCount: string,
      newEstateCount: string,
      newMansionCount: string,
      oldEstateCount: string,
      oldMansionCount: string,
      businessCount: string,
    }[];
    return data.map((item) => ({
      id: Number(item.id),
      encodedShopId: item.encodedShopId,
      name: item.name,
      tel: item.tel,
      address: {
        pref: {
          id: Number(item.prefId),
          name: item.prefName,
          kanaName: '',
        },
        city: {
          id: Number(item.cityId),
          name: item.cityName,
          kanaName: '',
          prefId: Number(item.prefId),
        },
        street: item.address,
        zipCode: item.zipCode,
      },
    }));
  },

  async getFcShopList(): Promise<Shop[]> {
    const response = await axios.get('/shop/list-by-wishes');
    const fcShops = response.data as {
      fcShopId: string,
      encodedShopId: string,
      fcShopName: string,
      fcShopUrl: string,
      fcName: string,
      fcShopFreeDial: string | null,
      fcShopTel: string,
      fcShopZipCode: string,
      fcShopAddress: string,
      fcShopBusinessHour: string,
      fcShopHoliday: string,
      fcShopLicenseNumber: string,
      fcShopImageName: string,
      fcShopImageName2: string,
      fcShopImageName3: string,
      fcShopFax: string,
      fcShopMail: string,
      cityName: string,
      prefName: string,
      daysOff: number[],
      access: { railName: string, stationName: string, walkMinutes: string }[],
      parkingQuantity: string,
      fcManagerName: string,
      joinGroup: string[],
      kasika: { pid: string, cug: string }
      workTimes: {
        sunday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        monday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        tuesday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        wednesday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        thursday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        friday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        saturday: { open: `${number}:${number}:${number}` | null, close: `${number}:${number}:${number}` | null },
        latitude: string,
        longitude: string,
        address: string
      }
    } [];
    return fcShops.map((data) => {
      return {
        id: Number(data.fcShopId),
        encodedShopId: data.encodedShopId,
        name: data.fcShopName,
        tel: data.fcShopTel,
        address: {
          pref: {
            id: 0,
            name: data.prefName,
            kanaName: '',
          },
          city: {
            id: 0,
            name: data.cityName,
            kanaName: '',
            prefId: 0,
          },
          street: data.fcShopAddress,
          zipCode: data.fcShopZipCode,
        }
      }
    });
  },
};
